<template>
  <div class="section" id="repayment-term-selection">
    <h1 class="title">What repayment term works best for you?</h1>
    <p class="description">
      We offer a variety of different repayment terms. Shorter repayment terms will generally have
      lower interest rates but the highest monthly payment. Longer repayment terms are likely to
      have the smallest monthly payment but a higher overall interest rate
    </p>
    <div class="buttons" id="repayment-terms">
      <button
        v-for="option in repaymentTermOptions"
        :key="option.repaymentTermId"
        class="repayment-option-button repayment-term-button"
        :class="[{ active: option?.repaymentTermId === selectedRepaymentTermId }]"
        @click="selectRepaymentTerm(option.repaymentTermId, option)"
      >
        <h5>{{ `${option.repaymentTerm}` }}</h5>
        <p>{{ `${option.repaymentTerm}` }} Estimated Rate Ranges:</p>
        <p>{{ option?.estimatedRateRanges }}</p>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RateTypeIds } from "@/config/constants";
import type { RepaymentTermOption } from "@/models/loanConfiguration";
import { ref, computed } from "vue";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps<{
  selectedRateTypeId?: number;
  selectedRateType: any;
}>();

const selectedRepaymentTermId = ref<number>(0);
const selectedRepaymentTermOption = ref<RepaymentTermOption | undefined>(undefined);
const appRatePlans = computed(() => {
  return store.getters["loanConfigurationModule/appRatePlans"];
});

const selectedDefaultRatePlan = computed(() => {
  return appRatePlans.value?.length === 1 ? appRatePlans.value[0] : undefined;
});

const selectedRatePlan = computed(() => store.state.loanConfigurationModule.selectedRatePlan);

const selectedRepaymentTerm = computed(() => {
  if (selectedRatePlan.value) {
    return selectedRatePlan.value;
  }
  return undefined;
});

const repaymentTermOption = computed<RepaymentTermOption | undefined>(() => {
  return repaymentTermOptions.value.find(
    (o: RepaymentTermOption) => o.repaymentTermId === selectedRepaymentTermId.value,
  );
});

const isValidRepaymentTerm = computed<boolean>(() => {
	return repaymentTermOption.value !== undefined;
});

const repaymentTerms = computed(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration.repaymentTermOptions,
);

const variableRateRepaymentTermOptions = computed<RepaymentTermOption[]>(
  () =>
    store.state.loanConfigurationModule.loanOptionsConfiguration.variableRateRepaymentTermOptions,
);
const fixedRateRepaymentTermOptions = computed<RepaymentTermOption[]>(
  () => store.state.loanConfigurationModule.loanOptionsConfiguration.fixedRateRepaymentTermOptions,
);

const repaymentTermOptions = computed<RepaymentTermOption[]>(() => {
  if (props.selectedRateTypeId === RateTypeIds.FIXED) {
    return fixedRateRepaymentTermOptions.value;
  }

  if (props.selectedRateTypeId === RateTypeIds.VARIABLE) {
    return variableRateRepaymentTermOptions.value;
  }

  return [];
});

function selectUpdatedRepaymentTermOption(repaymentTermId: number) {
  const repaymentTermOption = repaymentTermOptions.value.find(
    (o: RepaymentTermOption) => o.repaymentTermId === repaymentTermId,
  );

  if (repaymentTermOption) {
    selectRepaymentTerm(repaymentTermId, repaymentTermOption);

    return;
  }
  selectedRepaymentTermId.value = 0;
  selectedRepaymentTermOption.value = undefined;
}
function selectRepaymentTerm(repaymentTermId: number, repaymentTermOption: RepaymentTermOption) {
  selectedRepaymentTermId.value = repaymentTermId;
  selectedRepaymentTermOption.value = repaymentTermOption;
  // if (ratePlan) {
  //   store.dispatch("loanConfigurationModule/setSelectedRatePlan", ratePlan);
  // }
}

function selectDefaultRatePlan() {
  // if only one rate plan for this term select it
  if (selectedDefaultRatePlan.value) {
    store.dispatch("loanConfigurationModule/setSelectedRatePlan", selectedDefaultRatePlan.value);
  }
}

function isActive(repaymentTermId: number) {
  return selectedRepaymentTermId.value === repaymentTermId;
}

function resetSelectedRepaymentTerm() {
  selectedRepaymentTermId.value = 0;
  store.dispatch("loanConfigurationModule/setSelectedRatePlan", null);
}

defineExpose({
  appRatePlans,
  isActive,
	isValidRepaymentTerm,
  repaymentTerms,
	repaymentTermOption,
  repaymentTermOptions,
  resetSelectedRepaymentTerm,
  selectedRatePlan,
  selectedRepaymentTerm,
  selectedRepaymentTermId,
  selectedRepaymentTermOption,
  selectUpdatedRepaymentTermOption,
  selectRepaymentTerm,
  selectDefaultRatePlan,
  selectedDefaultRatePlan,
});
</script>
<style scoped src="@/styles/professional-grad/button.css"></style>
<style scoped src="@/styles/professional-grad/substep.css"></style>
<style scoped src="@/styles/professional-grad/substep-3.css"></style>
